import { useInView } from "react-intersection-observer"
import * as styles from "./styles.module.scss"
import Button from "../Button"
import cx from "classnames"
import Link from "gatsby-link"
import MediaPlayButton from "../MediaPlayButton"
import * as React from "react"

export interface HeroCircleProps {
  buttonCta?: string
  buttonLink?: string
  directSourceUrl?: string
  eyebrow?: string
  heading: string
  headingSize: string
  imgAlt?: string
  imgSrcLarge?: string
  imgSrcLargeWidth?: string
  imgSrcSmall?: string
  imgSrcSmallWidth?: string
  metadata?: string
  poster?: string
  posterVideoSrc?: string
  preload?: string
  subhead: string
  subheadSize: string
}

const shouldPreload = ({
  preload,
  poster,
  metadata,
}: {
  preload?: string
  poster?: string
  metadata?: string
}) => {
  if (preload) {
    return preload
  }
  if (poster) {
    return "none"
  }
  return metadata
}

const HeroCircle: React.FC<HeroCircleProps> = ({
  buttonCta,
  buttonLink,
  directSourceUrl,
  eyebrow,
  heading,
  headingSize,
  imgAlt,
  imgSrcLarge,
  imgSrcLargeWidth,
  imgSrcSmall,
  imgSrcSmallWidth,
  metadata,
  poster,
  posterVideoSrc,
  preload,
  subhead,
  subheadSize,
}) => {
  const [ref, inView, entry] = useInView({ threshold: 1 })

  return (
    <section
      className={cx({
        [styles.heroCircle]: true,
        [styles.subnav]: true,
        [styles.inView]: inView,
      })}
      ref={ref}
    >
      <div className={styles.pagePadding}>
        <div className={styles.messageWrap}>
          <div
            className={cx({
              [styles.message]: true,
              [styles.left]: true,
              [styles.invert]: true,
            })}
          >
            {eyebrow && <h6 className={styles.eyebrow}>{eyebrow}</h6>}
            <h3
              className={cx({
                [styles.heading]: true,
                [styles[headingSize]]: headingSize,
              })}
            >
              {heading}
            </h3>
            {subhead && (
              <p
                className={cx({
                  [styles.subhead]: true,
                  [styles[subheadSize]]: subheadSize,
                })}
              >
                {subhead}
              </p>
            )}
          </div>
          {buttonCta && buttonLink && (
            <div className={styles.actions}>
              <Link to={buttonLink}>
                <Button type="primary" size="lg" halo={true}>
                  {buttonCta}
                </Button>
              </Link>
            </div>
          )}
        </div>
        <div className={styles.circleWrapper}>
          <div className={styles.circle}>
            {imgSrcSmall && (
              <figure className={styles.figure}>
                <img
                  className={styles.image}
                  src={imgSrcSmall}
                  alt={imgAlt}
                  srcSet={`${imgSrcLarge} ${imgSrcLargeWidth}, ${imgSrcSmall} ${imgSrcSmallWidth}`}
                  sizes="40vw"
                />
              </figure>
            )}
            {posterVideoSrc && directSourceUrl && (
              <Link
                className={cx({
                  [styles.popupTrigger]: true,
                  ["js-modal-video"]: true,
                })}
                to={directSourceUrl}
              >
                <video
                  className={styles.video}
                  autoPlay
                  muted
                  playsInline
                  loop
                  poster={poster}
                  preload={shouldPreload({ metadata, poster, preload })}
                >
                  <source src={posterVideoSrc} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div className={styles.play}>
                  <MediaPlayButton />
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroCircle
